'use client';

import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import { WishlistFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistFragmentFragmentDoc';
import { type FC, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { GTMEventGlycerinDisplayWishlist } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { WishlistLineItemsFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistLineItemsFragmentFragmentDoc';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { buildGlycerinWishlistProducts } from '@packages/modules/src/helpers/buildGlycerinObject/buildGlycerinObject';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { KimRating } from '@packages/modules/src/KimRating/KimRating.client';
import { WishlistLineItem } from '../LineItem/WishlistLineItem';
import { GalleryViewItem } from '../LineItem/GalleryViewItem';

/* GraphQL */ `
  fragment WishlistFragment on Wishlist {
    lineItems {
      ...WishlistLineItemsFragment
    }
    count
  }
`;
interface WishlistProps {
  data?: FragmentType<typeof WishlistFragmentFragmentDoc>;
}
const messages = defineMessages({
  headline: {
    id: 'Wishlist.FullWishlist.Headline',
    defaultMessage: 'Merkzettel',
  },
  subHeadline: {
    id: 'Wishlist.FullWishlist.SubHeadline',
    defaultMessage:
      'Es befinden sich {count} Artikel auf deinem Merkzettel. Speichere weitere Artikel, um sie nicht aus den Augen zu verlieren.',
  },
  listView: {
    id: 'Wishlist.FullWishlist.ListView',
    defaultMessage: 'Listenansicht',
  },
  galleryView: {
    id: 'Wishlist.FullWishlist.GalleryView',
    defaultMessage: 'Galerieansicht',
  },
  changeproductSuccess: {
    defaultMessage: 'Ihr Artikel wurde geändert',
    id: 'Wishlist.changeProduct.success',
  },
});

export const Wishlist: FC<WishlistProps> = ({ data }) => {
  const { formatMessage } = useIntl();
  const fragmentData = getFragmentData(WishlistFragmentFragmentDoc, data);
  const dispatchGTMEvent = useTracking();

  const [viewMode, setViewMode] = useState<'listView' | 'galleryView'>('listView');
  const { lineItems, count } = fragmentData || {};

  useEffect(() => {
    dispatchGTMEvent<GTMEventGlycerinDisplayWishlist>({
      event: 'DisplayWishList',
      DisplayWishListData: {
        products: buildGlycerinWishlistProducts(
          getFragmentData(WishlistLineItemsFragmentFragmentDoc, lineItems),
        ),
      },
      _clear: true,
    });
  }, [dispatchGTMEvent, lineItems]);

  return (
    <>
      <div className="mx-1.5 text-center md:mx-2.5 md:text-left lg:mx-0">
        <h1 className="text-size-headline-2xl mt-0 mb-0.5 md:mt-[1.063rem] md:mb-[0.938rem]">
          {formatMessage(messages.headline)}
        </h1>
        {!!count && count > 0 && (
          <p className="text-size-body-sm mb-2.5">
            {formatMessage(messages.subHeadline, { count })}
          </p>
        )}
      </div>
      <div className="mx-auto flex w-full flex-1 justify-center md:justify-start">
        <button
          type="button"
          aria-label={formatMessage(messages.listView)}
          onClick={() => setViewMode('listView')}
          className={cn(
            'cursor-pointer',
            'hover:bg-color-grey-light flex grow items-center justify-center gap-0.5 border-b-2 xl:grow-0 xl:px-4',
            viewMode === 'listView'
              ? 'border-b-color-primary'
              : 'border-b-color-text-lighttransparent hover:border-b-color-grey-light',
          )}
        >
          {formatMessage(messages.listView)}
          <div className="icon-emp-list size-4" />
        </button>
        <button
          type="button"
          aria-label={formatMessage(messages.galleryView)}
          onClick={() => setViewMode('galleryView')}
          className={cn(
            'cursor-pointer',
            'hover:bg-color-grey-light flex grow items-center justify-center gap-0.5 border-b-2 xl:grow-0 xl:px-4',
            viewMode === 'galleryView'
              ? 'border-b-color-primary'
              : 'border-b-color-text-lighttransparent hover:border-b-color-grey-light',
          )}
        >
          {formatMessage(messages.galleryView)}
          <div className="icon-emp-gallery size-4" />
        </button>
      </div>

      <div className="bg-color-grey-light p-1.5 md:p-3">
        {viewMode === 'listView' &&
          lineItems &&
          lineItems.map((item, index) => (
            <WishlistLineItem
              key={getFragmentData(WishlistLineItemsFragmentFragmentDoc, item)?.sku || index}
              data={item}
            />
          ))}
        {viewMode === 'galleryView' && (
          <div
            className="h-full columns-2 items-start gap-1.5 md:columns-3 lg:columns-4"
            data-testid="gallery-view"
          >
            {lineItems &&
              lineItems.map((item, index) => (
                <GalleryViewItem
                  key={getFragmentData(WishlistLineItemsFragmentFragmentDoc, item)?.sku || index}
                  data={item}
                />
              ))}
          </div>
        )}
      </div>

      <ErrorBoundary>
        <KimRating pageType="wishlist" />
      </ErrorBoundary>
    </>
  );
};
